<script setup>
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { createReportAnalysis, downloadRecommendedCoverageTemplate } from '../../api/reportApi.js'
import AppAlert from '../../shared/components/AppAlert.vue'
import AppButton from '../../shared/components/AppButton.vue'
import FileDropzone from './components/FileDropzone.vue'
import { addUniqueFiles, createAnalysisFormData, fileKey } from './fileUtils.js'

const router = useRouter()
const healthFiles = ref([])
const insuranceFiles = ref([])
const supportingFiles = ref([])
const errors = reactive({ health: '', insurance: '', supporting: '', submit: '' })
const isSubmitting = ref(false)
const isDownloadingTemplate = ref(false)
const allFiles = computed(() => addUniqueFiles([], [
  ...healthFiles.value,
  ...insuranceFiles.value,
  ...supportingFiles.value
]))

function addFiles(incomingFiles, errorKey) {
  const target = { health: healthFiles, insurance: insuranceFiles, supporting: supportingFiles }[errorKey]
  target.value = addUniqueFiles(target.value, incomingFiles, errorKey !== 'supporting')
  errors[errorKey] = ''
}

function removeFile(file, fileType) {
  const target = { health: healthFiles, insurance: insuranceFiles, supporting: supportingFiles }[fileType]
  target.value = target.value.filter(item => fileKey(item) !== fileKey(file))
}

async function downloadTemplate() {
  if (isDownloadingTemplate.value) return

  isDownloadingTemplate.value = true
  errors.supporting = ''
  try {
    const { blob, filename } = await downloadRecommendedCoverageTemplate()
    if (!blob?.size) throw new Error('템플릿 파일이 비어 있습니다.')

    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename || 'recommended-coverage-template.xlsx'
    document.body.append(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
  } catch (error) {
    errors.supporting = error.message || '템플릿을 다운로드하지 못했습니다.'
  } finally {
    isDownloadingTemplate.value = false
  }
}

async function submitAnalysis() {
  if (isSubmitting.value) return
  if (!allFiles.value.length) {
    errors.submit = '분석할 PDF 또는 기준 Excel을 한 개 이상 선택하세요.'
    return
  }

  isSubmitting.value = true
  errors.submit = ''
  try {
    const result = await createReportAnalysis(createAnalysisFormData({
      healthFiles: healthFiles.value,
      insuranceFiles: insuranceFiles.value,
      supportingFile: supportingFiles.value[0] ?? null
    }))
    if (!result?.analysisId) throw new Error('분석 ID를 받지 못했습니다.')
    await router.push(`/analysis/${encodeURIComponent(result.analysisId)}`)
  } catch (error) {
    errors.submit = error.message || '분석을 완료하지 못했습니다.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <section class="upload-panel">
    <ol class="step-indicator" aria-label="분석 단계">
<li class="step-item is-active" aria-current="step"><span class="step-num" aria-hidden="true">1</span><div><b>현재 단계</b><span>분석 자료 올리기</span></div></li>
<li class="step-item"><span class="step-num" aria-hidden="true">2</span><div><b>2단계</b><span>AI 교차 분석</span></div></li>
<li class="step-item"><span class="step-num" aria-hidden="true">3</span><div><b>3단계</b><span>PDF 결과 받기</span></div></li>
</ol>
    <div class="page-intro">
      <p class="home-kicker">1단계 · 자료 올리기</p>
      <h1>분석할 자료를 올려 주세요</h1>
      <p class="page-lead">건강검진 결과와 보험 보장분석 PDF를 올리면 두 자료를 교차 분석합니다. 권장금액 기준표(Excel)는 선택입니다.</p>
    </div>
    <div class="upload-grid">
      <div>
        <FileDropzone
          label="건강검진 자료"
          hint="PDF · 여러 개 선택 가능"
          accept=".pdf"
          :mime-types="['application/pdf']"
          multiple
          :files="healthFiles"
          :disabled="isSubmitting"
          @add="addFiles($event, 'health')"
          @remove="removeFile($event, 'health')"
          @error="errors.health = $event"
        />
        <AppAlert v-if="errors.health">{{ errors.health }}</AppAlert>
      </div>
      <div>
        <FileDropzone
          label="보험 보장분석"
          hint="PDF · 한 개 선택"
          accept=".pdf"
          :mime-types="['application/pdf']"
          :files="insuranceFiles"
          :disabled="isSubmitting"
          @add="addFiles($event, 'insurance')"
          @remove="removeFile($event, 'insurance')"
          @error="errors.insurance = $event"
        />
        <AppAlert v-if="errors.insurance">{{ errors.insurance }}</AppAlert>
      </div>
      <div>
        <FileDropzone
          label="선택 권장금액"
          hint="XLS · XLSX · 한 개 선택"
          accept=".xls,.xlsx"
          :files="supportingFiles"
          :disabled="isSubmitting"
          @add="addFiles($event, 'supporting')"
          @remove="removeFile($event, 'supporting')"
          @error="errors.supporting = $event"
        >
          <template #label-action>
            <button
              type="button"
              class="app-button app-button--secondary app-button--sm template-download-button"
              :disabled="isSubmitting || isDownloadingTemplate"
              :aria-busy="isDownloadingTemplate"
              @click="downloadTemplate"
            >
              {{ isDownloadingTemplate ? '다운로드 중...' : '↓ 템플릿 다운로드' }}
            </button>
          </template>
        </FileDropzone>
        <p class="file-dropzone-help">미첨부 시 37개 담보의 기본 권장금액이 적용됩니다. 다운로드한 템플릿의 파란 금액 칸에 만원 단위 숫자만 입력하세요. 빈 금액은 기본값을 사용합니다.</p>
        <AppAlert v-if="errors.supporting">{{ errors.supporting }}</AppAlert>
      </div>
    </div>
    <AppAlert v-if="errors.submit">{{ errors.submit }}</AppAlert>
    <p v-if="isSubmitting" class="upload-status" role="status">
      <span class="loading-spinner" aria-hidden="true" />
      파일을 보내고 분석을 시작하는 중입니다. 파일 크기에 따라 몇 분이 걸릴 수 있습니다.
    </p>
    <div class="upload-actions">
      <p class="upload-count">선택한 파일 <b>{{ allFiles.length }}개</b></p>
      <AppButton size="lg" :disabled="isSubmitting || !allFiles.length" @click="submitAnalysis">
        {{ isSubmitting ? '분석 시작 중...' : '분석 시작' }} <span v-if="!isSubmitting" class="arrow">→</span>
      </AppButton>
    </div>
  </section>
</template>
